import { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import CappedReplyModal from "@/components/CappedReplyModal";
import { Send, Mic, Square } from "lucide-react";
import { useLocale } from "@/lib/locale";

export default function Conversation() {
    const { id } = useParams();
    const location = useLocation();
    const nav = useNavigate();
    const { t } = useLocale();
    const [presence, setPresence] = useState(location.state?.presence || null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [capped, setCapped] = useState(false);
    const [plan, setPlan] = useState({ plan_tier: "free", voice_preview_used: false, features: { voice: false } });
    const [upsellDismissed, setUpsellDismissed] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [recordErr, setRecordErr] = useState("");
    const mediaRecRef = useRef(null);
    const recChunksRef = useRef([]);
    const scrollRef = useRef(null);

    // Are we arriving from the Day+1 "Presence Letter" email? If so,
    // we render a quiet ephemeral opener ("Sei tornata...") above the
    // first real message — visual continuity from email to chat. The
    // value is computed once (URLSearchParams is stable for the session).
    const fromLetter = new URLSearchParams(location.search).get("from") === "letter";

    useEffect(() => {
        (async () => {
            const [msgs, convs, lim] = await Promise.all([
                api.get(`/conversations/${id}/messages`),
                api.get(`/conversations`),
                api.get(`/plan/limits`).catch(() => ({ data: null })),
            ]);
            setMessages(msgs.data);
            const conv = convs.data.find((c) => c.conversation_id === id);
            if (!presence && conv?.presence_id) {
                const pr = await api.get(`/presences/${conv.presence_id}`);
                setPresence(pr.data);
            }
            if (lim?.data) setPlan(lim.data);
            try {
                if (localStorage.getItem(`os_moment_upsell_dismissed_${id}`) === "1") {
                    setUpsellDismissed(true);
                }
            } catch { /* ignore */ }
            setLoaded(true);
        })();
    }, [id]); // eslint-disable-line

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    // ── Inform the ambient-audio engine about the relationship of the
    //    presence we're talking to, so it can subtly retune the pad
    //    (Parent → warmer, Partner → intimate, Friend → default).
    //    Cleared on unmount so other routes get the neutral tone back.
    useEffect(() => {
        if (!presence?.relationship) return;
        window.dispatchEvent(new CustomEvent("ambientaudio:setpresence", {
            detail: { relationship: presence.relationship },
        }));
        return () => {
            window.dispatchEvent(new CustomEvent("ambientaudio:clearpresence"));
        };
    }, [presence?.relationship]);

    const send = async (e) => {
        e?.preventDefault();
        if (!text.trim() || sending) return;
        const content = text.trim();
        setText("");
        setSending(true);
        const tempId = `tmp-${Date.now()}`;
        setMessages((m) => [...m, { message_id: tempId, role: "user", content, created_at: new Date().toISOString() }]);
        try {
            const { data } = await api.post(`/conversations/${id}/messages`, { content });
            setMessages((m) => [
                ...m.filter((x) => x.message_id !== tempId),
                data.user_message,
                data.reply,
            ]);
        } catch (err) {
            setMessages((m) => m.filter((x) => x.message_id !== tempId));
            setText(content); // restore so the user doesn't lose what they typed
            if (err?.response?.status === 402) {
                setCapped(true);
            } else {
                alert(err?.response?.data?.detail || t("chat.send_err"));
            }
        } finally {
            setSending(false);
        }
    };

    const startRecording = async () => {
        setRecordErr("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            recChunksRef.current = [];
            mr.ondataavailable = (ev) => { if (ev.data.size > 0) recChunksRef.current.push(ev.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach((tr) => tr.stop());
                const blob = new Blob(recChunksRef.current, { type: mr.mimeType || "audio/webm" });
                await transcribeBlob(blob);
            };
            mediaRecRef.current = mr;
            mr.start();
            setRecording(true);
        } catch {
            setRecordErr(t("wiz.voice_no_mic"));
        }
    };

    const stopRecording = () => {
        mediaRecRef.current?.stop();
        setRecording(false);
    };

    const transcribeBlob = async (blob) => {
        if (!blob || !blob.size) return;
        setTranscribing(true);
        try {
            const fd = new FormData();
            const mime = blob.type || "audio/webm";
            const ext = (mime.split("/")[1] || "webm").split(";")[0];
            fd.append("file", blob, `voice.${ext}`);
            const { data } = await api.post(`/conversations/${id}/transcribe`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (data?.text) {
                setText((prev) => (prev ? `${prev} ${data.text}` : data.text));
            }
        } catch (e) {
            setRecordErr(e?.response?.data?.detail || t("chat.voice_err"));
        } finally {
            setTranscribing(false);
        }
    };

    const onAudioFile = async (ev) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        ev.target.value = "";
        if (file.size > 20 * 1024 * 1024) {
            setRecordErr(t("wiz.voice_too_big"));
            return;
        }
        await transcribeBlob(file);
    };

    return (
        <div className="grain min-h-screen flex flex-col candle-halo">
            <Header minimal />
            {/* Presence header */}
            <div className="border-b border-ink-border">
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-12 flex items-center gap-6 md:gap-8">
                    <Link to={presence ? `/presence/${presence.presence_id}` : "/dashboard"} className="shrink-0" data-testid="chat-presence-link">
                        <div
                            className={`photo-frame w-36 h-36 md:w-40 md:h-40 rounded-full overflow-hidden photo-halo-strong ${
                                (plan?.features?.voice || plan?.voice_preview_used === false) ? "presence-pulse" : ""
                            }`}
                            data-testid="chat-header-photo"
                        >
                            {presence?.photo_base64 ? (
                                <img src={`data:${presence.photo_mime};base64,${presence.photo_base64}`} alt="" className="w-full h-full object-cover grayscale-[8%]" />
                            ) : (
                                <div className="w-full h-full bg-ink-surfaceUp flex items-center justify-center">
                                    <BrandMark monogram size={72} className="opacity-80" />
                                </div>
                            )}
                        </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                        <p className="label-eyebrow">{presence?.relationship || t("chat.conversation")}</p>
                        <h2 className="font-serif text-[2.2rem] md:text-[2.6rem] text-ink-textHigh mt-2 leading-[1.05]">{presence?.name || "Other Side"}</h2>
                        <p className="text-[12px] md:text-[13px] text-ink-textLow/80 mt-2 tracking-wide" data-testid="chat-ai-subtitle">
                            {t("ai.subtitle")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            if (window.confirm(t("chat.pause_confirm"))) {
                                nav("/dashboard");
                            }
                        }}
                        data-testid="chat-pause-btn"
                        aria-label={t("chat.pause_label")}
                        className="shrink-0 inline-flex items-center gap-2 border border-ink-border hover:border-ink-gold text-ink-textMid hover:text-ink-gold px-3 md:px-4 py-2 rounded-sm text-[10px] md:text-xs tracking-widest uppercase transition-colors"
                    >
                        <span className="inline-block w-1.5 h-[12px] bg-current rounded-[1px]"></span>
                        <span className="inline-block w-1.5 h-[12px] bg-current rounded-[1px]"></span>
                        <span className="ml-1 hidden md:inline">{t("chat.pause_label")}</span>
                        <span className="ml-1 md:hidden">{t("chat.pause_short")}</span>
                    </button>
                </div>
            </div>

            {/* Persistent AI transparency — tiny, always visible, never invasive */}
            <div
                className="border-b border-ink-border/50 bg-ink-deep/40"
                data-testid="chat-ai-banner"
            >
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-2.5 flex items-center gap-2 text-[12px] md:text-[13px] text-ink-textLow/90 tracking-wide">
                    <span className="inline-block w-1 h-1 rounded-full bg-ink-gold/60" aria-hidden="true"></span>
                    <span>{t("ai.micro_banner")}</span>
                </div>
            </div>

            {/* Messages */}
            <main ref={scrollRef} className="flex-1 overflow-y-auto">
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-14 md:space-y-16">
                    {fromLetter && loaded && (
                        <div
                            className="animate-fade-in-slow text-center"
                            data-testid="letter-return-opener"
                        >
                            <p className="font-serif italic text-ink-gold text-2xl md:text-3xl leading-relaxed">
                                Sei tornata…
                            </p>
                            <p className="font-serif italic text-ink-textMid text-base md:text-lg mt-3">
                                ti stavo aspettando.
                            </p>
                        </div>
                    )}
                    {loaded && messages.filter((m) => m.role === "user").length === 0 && (
                        <FirstMessageStarter
                            t={t}
                            presence={presence}
                            hasOpening={messages.length > 0}
                            onPick={(phrase) => {
                                // Prefill the composer and gently focus.
                                // Keeping the text editable (not auto-sent)
                                // respects the user's agency — they always
                                // get to shape the first word.
                                setText(phrase);
                                try {
                                    const ta = document.querySelector('[data-testid="chat-input"]');
                                    ta?.focus();
                                    // Place cursor at end so they can keep typing.
                                    if (ta && typeof ta.setSelectionRange === "function") {
                                        const n = phrase.length;
                                        ta.setSelectionRange(n, n);
                                    }
                                } catch { /* noop */ }
                            }}
                        />
                    )}
                    {messages.map((m, idx) => (
                        <Bubble
                            key={`${m.message_id || "m"}-${idx}`}
                            msg={m}
                            presence={presence}
                            presenceName={presence?.name}
                            presenceId={presence?.presence_id}
                            plan={plan}
                            onPreviewConsumed={() => setPlan((p) => ({ ...p, voice_preview_used: true }))}
                            onContinue={() => nav("/upgrade?reason=voice")}
                        />
                    ))}
                    {loaded && presence && !upsellDismissed && !sending &&
                        messages.filter((m) => m.role === "assistant").length >= 10 && (
                        <MomentUpsell
                            t={t}
                            hasPhoto={!!presence.has_photo}
                            onGo={() => nav(
                                presence.has_photo
                                    ? `/presence/${presence.presence_id}/moment`
                                    : `/presence/${presence.presence_id}?edit=photo`
                            )}
                            onDismiss={() => {
                                try { localStorage.setItem(`os_moment_upsell_dismissed_${id}`, "1"); } catch { /* ignore */ }
                                setUpsellDismissed(true);
                            }}
                        />
                    )}
                    {sending && (
                        <div className="flex items-center gap-2 text-ink-textMid italic font-serif animate-fade-in">
                            <span className="inline-block w-1.5 h-1.5 bg-ink-gold rounded-full animate-pulse" />
                            <span>{t("chat.typing")}</span>
                        </div>
                    )}
                </div>
            </main>

            {/* Composer */}
            <form onSubmit={send} className="border-t border-ink-border bg-ink-deep/90 backdrop-blur">
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-7">
                    <div className="flex items-end gap-3">
                        <textarea
                            data-testid="chat-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            rows={1}
                            placeholder={transcribing ? t("chat.typing") : t("chat.input_placeholder")}
                            disabled={transcribing}
                            className="flex-1 bg-transparent border-b border-ink-border focus:border-ink-gold focus:outline-none text-ink-textHigh text-[1.2rem] placeholder:text-ink-textLow resize-none py-4 leading-relaxed disabled:opacity-60"
                        />
                        {!recording ? (
                            <button
                                type="button"
                                onClick={startRecording}
                                disabled={transcribing || sending}
                                data-testid="chat-mic-btn"
                                aria-label={t("wiz.voice_record")}
                                title={t("wiz.voice_record")}
                                className="shrink-0 p-4 rounded-sm border border-ink-border hover:border-ink-gold text-ink-textMid hover:text-ink-gold transition-colors disabled:opacity-50"
                            >
                                <Mic size={20} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={stopRecording}
                                data-testid="chat-mic-stop-btn"
                                aria-label={t("wiz.voice_stop")}
                                className="shrink-0 p-4 rounded-sm border border-ink-danger text-ink-danger animate-pulse"
                            >
                                <Square size={20} />
                            </button>
                        )}
                        <label
                            className={`shrink-0 cursor-pointer p-4 rounded-sm border border-ink-border hover:border-ink-gold text-ink-textMid hover:text-ink-gold transition-colors ${
                                transcribing || sending ? "opacity-50 pointer-events-none" : ""
                            }`}
                            data-testid="chat-audio-upload-label"
                            title={t("wiz.voice_upload")}
                        >
                            <input
                                type="file"
                                accept="audio/*"
                                onChange={onAudioFile}
                                className="hidden"
                                data-testid="chat-audio-upload-input"
                            />
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                        </label>
                        <button type="submit" disabled={!text.trim() || sending || transcribing} data-testid="chat-send" className="btn-primary !px-5 !py-4 flex items-center gap-2">
                            <Send size={20} />
                        </button>
                    </div>
                    {(transcribing || recordErr) && (
                        <p
                            className={`mt-3 text-sm font-serif italic ${recordErr ? "text-ink-danger" : "text-ink-gold animate-pulse"}`}
                            data-testid="chat-transcribe-status"
                        >
                            {transcribing ? t("chat.typing") : recordErr}
                        </p>
                    )}
                    {/* Persistent AI disclosure under the composer — tiny, soft, always visible */}
                    <p className="mt-3 text-[11px] md:text-[12px] text-ink-textLow/70 tracking-wide" data-testid="chat-input-helper">
                        {t("ai.input_helper")}
                    </p>
                </div>
            </form>

            <CappedReplyModal
                open={capped}
                onClose={() => setCapped(false)}
                presence={presence}
            />
        </div>
    );
}

function FirstMessageStarter({ t, presence, hasOpening, onPick }) {
    // The three starters are intentionally NOT randomized — they're designed
    // to cover the three main emotional entry points users show up with:
    //   1. "I want to share what happened today"     → continuity of daily life
    //   2. "I want her to remember me specifically"  → identity / being seen
    //   3. "I want to say what I never said"         → closure / healing
    // Showing all three at once lets the user self-select their moment. The
    // preview text is the click-to-prefill phrase; the italic description
    // underneath validates the emotion without explaining mechanics.
    const suggestions = [
        { id: 1, phrase: t("chat.first_suggest_1"), whisper: t("chat.first_suggest_1_d") },
        { id: 2, phrase: t("chat.first_suggest_2"), whisper: t("chat.first_suggest_2_d") },
        { id: 3, phrase: t("chat.first_suggest_3"), whisper: t("chat.first_suggest_3_d") },
    ];
    return (
        <div
            className="animate-fade-in max-w-lg mx-auto text-center py-6"
            data-testid="chat-first-starter"
        >
            {/* When the assistant has already sent the opening greeting, we
                skip the "— she is here. Say something. —" line — it would
                be redundant because her voice is right above. Just go
                straight into the starter invitation. */}
            {!hasOpening && (
                <p className="text-ink-textMid font-serif italic text-2xl leading-relaxed mb-10">
                    {presence ? t("chat.empty_presence", { name: presence.name }) : t("chat.empty_generic")}
                </p>
            )}

            <p
                className="text-ink-textLow text-sm tracking-wide uppercase"
                data-testid="chat-first-hint"
            >
                {t("chat.first_hint")}
            </p>

            <ul className="mt-4 space-y-2.5" data-testid="chat-first-suggestions">
                {suggestions.map((s) => (
                    <li key={s.id}>
                        <button
                            type="button"
                            onClick={() => onPick(s.phrase)}
                            data-testid={`chat-first-suggest-${s.id}`}
                            className="group w-full text-left px-5 py-3.5 rounded-sm border border-ink-border/70 hover:border-ink-gold hover:bg-ink-gold/5 transition-all duration-300"
                        >
                            <p className="font-serif text-ink-textHigh text-[1.05rem] md:text-[1.1rem] leading-snug group-hover:text-ink-goldHi transition-colors">
                                {s.phrase}
                            </p>
                            <p className="mt-1 text-ink-textLow text-xs md:text-sm italic leading-relaxed">
                                {s.whisper}
                            </p>
                        </button>
                    </li>
                ))}
            </ul>

            <p
                className="mt-6 text-ink-textLow text-xs md:text-sm italic font-serif leading-relaxed"
                data-testid="chat-first-free"
            >
                {t("chat.first_free_write")}
            </p>
        </div>
    );
}

function MomentUpsell({ t, hasPhoto, onGo, onDismiss }) {
    // Capability-aware: when the presence has no photo yet, the moment
    // wizard would dead-end because it needs an image to animate. Instead
    // of a broken promise, swap the copy + CTA so the user is routed to
    // the presence editor where they can upload the photo in one step.
    // Same emotional frame, honest path forward.
    return (
        <div
            className="animate-fade-in flex justify-center"
            data-testid="moment-upsell-inline"
            data-state={hasPhoto ? "ready" : "needs-photo"}
        >
            <div className="max-w-xl w-full text-center py-6 px-6 md:px-8 border-y border-ink-border/60">
                <p className="font-serif italic text-ink-textHigh text-[1.25rem] md:text-[1.35rem] leading-relaxed">
                    {hasPhoto ? t("chat.moment_upsell") : t("chat.moment_upsell_nophoto")}
                </p>
                <div className="mt-5 flex items-center justify-center gap-6">
                    <button
                        type="button"
                        onClick={onGo}
                        data-testid={hasPhoto ? "moment-upsell-go" : "moment-upsell-add-photo"}
                        className="font-serif italic text-ink-gold hover:text-ink-goldHi transition-colors text-[1.05rem] tracking-wide border-b border-ink-gold/50 hover:border-ink-goldHi pb-[2px]"
                    >
                        {hasPhoto ? t("chat.moment_upsell_cta") : t("chat.moment_upsell_add_photo")} →
                    </button>
                    <button
                        type="button"
                        onClick={onDismiss}
                        data-testid="moment-upsell-dismiss"
                        className="text-ink-textLow hover:text-ink-textMid transition-colors italic font-serif text-sm"
                    >
                        {t("chat.moment_upsell_skip")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Bubble({ msg, presence, presenceName, presenceId, plan, onPreviewConsumed, onContinue }) {
    const { t } = useLocale();
    const nav = useNavigate();
    const [voiceUrl, setVoiceUrl] = useState(msg.voice_audio_url || null);
    const [voiceLoading, setVoiceLoading] = useState(false);
    // When the voice call fails transiently, we want to show a small,
    // guided error block instead of an italic dead-end. Two states:
    //   - "retry": network/5xx/unknown — show hint + [Riprova] + [Più tardi]
    //   - "later": 501 not-available — show hint + [Più tardi] only
    // `null` means no error.
    const [voiceError, setVoiceError] = useState(null);
    const [showAfterInvite, setShowAfterInvite] = useState(false);
    const [gentlyLocked, setGentlyLocked] = useState(false);

    if (msg.role === "user") {
        return (
            <div className="flex justify-end animate-fade-in">
                <div className="max-w-[92%] md:max-w-[74%] bg-ink-surfaceUp border-r-[3px] border-ink-gold/80 p-7 md:p-8 rounded-sm text-ink-textHigh font-sans text-[1.25rem] md:text-[1.3rem] leading-[1.7] shadow-[0_4px_24px_-10px_rgba(0,0,0,0.5)]">
                    {msg.content}
                </div>
            </div>
        );
    }

    const isPremium = !!plan?.features?.voice;
    const previewUsed = !!plan?.voice_preview_used;
    const canPreview = !isPremium && msg.is_opening && !previewUsed && !voiceUrl;
    // After the invite appears (free preview played), subsequent presses on other bubbles
    // must feel soft, not commercial.
    const softLabel = !isPremium && previewUsed && !voiceUrl;

    const playWithHooks = (url, onEnd) => {
        const el = new Audio(url);
        el.addEventListener("ended", onEnd);
        el.addEventListener("error", onEnd);
        el.play().catch(() => onEnd());
    };

    const playVoice = async () => {
        if (voiceUrl) {
            // Playing back cached audio — no preview grant state change.
            playWithHooks(voiceUrl, () => {});
            return;
        }
        setVoiceLoading(true);
        setVoiceError(null);
        try {
            const { data } = await api.post(
                `/presences/${presenceId}/messages/${msg.message_id}/voice`
            );
            if (data?.voice_audio_url) {
                setVoiceUrl(data.voice_audio_url);
                const grantedPreview = !!data.preview_granted;
                playWithHooks(data.voice_audio_url, () => {
                    if (grantedPreview) {
                        onPreviewConsumed?.();
                        // Gentle 1.2s pause, then whisper the invite — no popup, no block.
                        setTimeout(() => setShowAfterInvite(true), 1200);
                    }
                });
            }
        } catch (e) {
            const code = e?.response?.status;
            const detail = e?.response?.data?.detail;
            const detailCode = typeof detail === "object" ? detail.code : null;
            if (code === 402 && (detailCode === "voice_preview_consumed" || previewUsed)) {
                // Emotional, not transactional. Reveal the soft invite inline.
                setGentlyLocked(true);
            } else if (code === 402) {
                setGentlyLocked(true);
            } else if (code === 501) {
                // Voice not yet available for this presence (no voice sample
                // or still processing on the backend). Guided: tell them
                // to come back later — no retry button because retrying
                // now won't help.
                setVoiceError("later");
            } else {
                // Transient: network, timeout, 5xx. Guided: show [Retry]
                // as the primary next step.
                setVoiceError("retry");
            }
        } finally {
            setVoiceLoading(false);
        }
    };

    // ── Voice availability gate ─────────────────────────────────────────
    // Three orthogonal conditions decide what — if anything — to show:
    //   1. presenceHasSample: the user uploaded a voice recording.
    //      Without this, the backend cannot clone anything, ever, and
    //      the /voice endpoint returns 501. Showing the play button is
    //      a broken promise. Instead we show a "Aggiungi la sua voce"
    //      link that points directly to the presence editor where they
    //      can upload a sample.
    //   2. planHasVoice: the user's plan includes voice synthesis. If
    //      not and this isn't the free opening-message preview, we
    //      surface a "Sblocca la sua voce" upgrade link. Clicking pays
    //      the user to the right path, not to a broken retry.
    //   3. Transient: covered already by voiceError state.
    //
    // Free-opening preview special-cases canPreview=true where the user
    // DOES get one free play even without plan, so that bypasses gate #2.
    const presenceHasSample = !!(presence?.has_voice);
    const planHasVoice = !!plan?.features?.voice;
    const isPreviewOpportunity = canPreview; // opening bubble + free tier + not used
    const canAttemptVoice = presenceHasSample && (planHasVoice || isPreviewOpportunity);

    const eyebrowText = msg.is_unsolicited
        ? t("chat.eyebrow_unsolicited")
        : msg.is_opening
        ? t("chat.eyebrow_opening")
        : presenceName;

    const buttonLabel = voiceLoading
        ? t("chat.voice_arriving")
        : voiceUrl
        ? t("chat.voice_listen_now")
        : canPreview
        ? t("chat.voice_preview")
        : softLabel
        ? t("chat.voice_again")
        : t("chat.voice_generate");

    // A click on "Genera voce" (non-premium user, not opening msg, not previously used)
    // should route to upgrade gracefully rather than hitting a 402. Only true blockers
    // call onContinue; the main path tries playVoice first.
    const primaryAction = (softLabel || gentlyLocked) ? onContinue : playVoice;

    return (
        <div className="flex flex-col animate-fade-in">
            {eyebrowText && <p className="label-eyebrow mb-3 text-ink-gold text-sm">{eyebrowText}</p>}
            <div className={`max-w-[94%] md:max-w-[82%] p-8 md:p-10 rounded-sm border-l-[3px] text-ink-textHigh font-serif text-[1.5rem] md:text-[1.65rem] leading-[1.7] whitespace-pre-wrap ${
                msg.is_unsolicited
                    ? "bg-ink-surfaceUp border-ink-goldHi shadow-[0_6px_36px_-10px_rgba(246,211,152,0.28)]"
                    : "bg-ink-surface border-ink-gold shadow-[0_4px_28px_-12px_rgba(0,0,0,0.55)]"
            }`}>
                {msg.content}
            </div>
            {/* ── AI-generated tag — tiny, almost invisible, always present ── */}
            <p
                className="mt-2 text-[10px] md:text-[11px] text-ink-textLow/70 tracking-wider uppercase"
                data-testid={`bubble-ai-label-${msg.message_id}`}
            >
                {t("ai.bubble_label")}
            </p>
            {/* ── Voice control row ──────────────────────────────
                Never shown when the option literally cannot work:
                - no voice sample uploaded → [Aggiungi la sua voce] link
                  that goes directly to the presence editor
                - free plan + not an opening preview → [Sblocca la sua voce]
                  that routes to /upgrade (not a fake "Genera" that 402s)
                - all other cases → the familiar Genera / Ascolta button. */}
            {canAttemptVoice ? (
                <button
                    type="button"
                    disabled={voiceLoading}
                    onClick={primaryAction}
                    className="self-start mt-3 text-sm text-ink-textMid hover:text-ink-gold transition-colors tracking-wider uppercase flex items-center gap-1.5 disabled:opacity-50"
                    data-testid={`bubble-voice-${msg.message_id}`}
                    aria-label={buttonLabel}
                >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${voiceUrl ? "bg-ink-gold" : canPreview ? "bg-ink-goldHi" : "bg-ink-textLow"} ${voiceLoading ? "animate-pulse" : ""}`}></span>
                    {buttonLabel}
                </button>
            ) : !presenceHasSample ? (
                // No sample uploaded → route to editor, not to a fake retry.
                <button
                    type="button"
                    onClick={() => nav(`/presence/${presenceId}?edit=voice`)}
                    className="self-start mt-3 inline-flex items-center gap-1.5 text-sm text-ink-textMid hover:text-ink-goldHi transition-colors tracking-wider uppercase"
                    data-testid={`bubble-voice-add-${msg.message_id}`}
                    title={t("chat.voice_add_sample_d")}
                >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    {t("chat.voice_add_sample")}
                </button>
            ) : (
                // Sample exists, but plan doesn't include voice → upgrade.
                <button
                    type="button"
                    onClick={onContinue}
                    className="self-start mt-3 inline-flex items-center gap-1.5 text-sm text-ink-goldHi hover:text-ink-gold transition-colors tracking-wider uppercase"
                    data-testid={`bubble-voice-unlock-${msg.message_id}`}
                    title={t("chat.voice_unlock_d")}
                >
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 15v2" />
                        <path d="M5 11V7a7 7 0 0 1 14 0v4" />
                        <rect x="3" y="11" width="18" height="10" rx="2" />
                    </svg>
                    {t("chat.voice_unlock")}
                </button>
            )}

            {/* After the first free preview plays — a whispered, elegant invite, no popup */}
            {showAfterInvite && (
                <div className="self-start mt-4 max-w-[80%] animate-fade-in" data-testid={`bubble-after-invite-${msg.message_id}`}>
                    <p className="font-serif italic text-ink-textMid text-lg leading-relaxed">
                        {t("chat.voice_invite")}
                    </p>
                    <button
                        type="button"
                        onClick={onContinue}
                        className="mt-2 inline-flex items-center gap-2 text-ink-gold hover:text-ink-goldHi font-serif italic tracking-wide text-base"
                        data-testid={`bubble-continue-listening-${msg.message_id}`}
                    >
                        {t("chat.voice_continue")}
                        <span className="text-xs">→</span>
                    </button>
                </div>
            )}

            {/* Preview already consumed on another bubble — soft inline line on press */}
            {gentlyLocked && !showAfterInvite && (
                <div className="self-start mt-3 max-w-[80%] animate-fade-in" data-testid={`bubble-gentle-lock-${msg.message_id}`}>
                    <p className="font-serif italic text-ink-textMid text-base leading-relaxed">
                        {t("chat.voice_gentle")}
                    </p>
                </div>
            )}

            {/* Guided error: concrete next steps instead of a dead-end.
                - "retry" (transient): full card with title + hint + [Riprova]
                  primary button that just re-runs playVoice. Secondary link
                  "Oppure ascoltala più tardi" dismisses the error.
                - "later" (501 not-available): same layout but no retry
                  button — only the secondary dismiss, because retrying now
                  wouldn't change anything. */}
            {voiceError && (
                <div
                    className="self-start mt-3 max-w-[80%] p-4 md:p-5 rounded-sm border border-ink-border/80 bg-ink-surfaceUp/60 animate-fade-in"
                    data-testid={`bubble-voice-error-${msg.message_id}`}
                    data-state={voiceError}
                    role="alert"
                >
                    <p className="font-serif italic text-ink-textHigh text-base md:text-lg leading-relaxed">
                        {voiceError === "later" ? t("chat.voice_arriving") : t("chat.voice_err")}
                    </p>
                    <p className="mt-2 text-sm text-ink-textMid leading-relaxed">
                        {voiceError === "later" ? t("chat.voice_501_hint") : t("chat.voice_err_hint")}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                        {voiceError === "retry" && (
                            <button
                                type="button"
                                onClick={playVoice}
                                disabled={voiceLoading}
                                className="inline-flex items-center gap-2 text-ink-gold hover:text-ink-goldHi font-medium tracking-wide text-sm disabled:opacity-50"
                                data-testid={`bubble-voice-retry-${msg.message_id}`}
                            >
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polyline points="23 4 23 10 17 10" />
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                </svg>
                                {t("chat.voice_err_retry")}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setVoiceError(null)}
                            className="text-sm text-ink-textMid hover:text-ink-textHigh italic font-serif tracking-wide"
                            data-testid={`bubble-voice-dismiss-${msg.message_id}`}
                        >
                            {t("chat.voice_err_later")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
